/**
 * Duplicate Order Warning Modal — Premium SaaS redesign
 *
 * Pixel-level spec source: Duplicate Order Modal UI Spec (Feb 22, 2026).
 * Shown when a user tries to create an order that matches:
 *   - Same normalized property address
 *   - Same client code
 *   - Same product type
 *   - Existing order is in active status (not Complete/Cancelled)
 *
 * Design goals:
 *   - Decision checkpoint, not a warning popup
 *   - Clear hierarchy: review first, override second
 *   - Premium indigo (danger implied by wording, not color)
 *   - Fully responsive (single column + stacked buttons < 640px)
 */

import React from 'react';
import { AlertTriangle, ExternalLink, X, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { formatDateTimeLocal } from '../../utils/formatters';

/* ---------- Status pill palette (per spec) ---------- */
const STATUS_PILL = {
  clarification:            { bg: '#FEF2F2', fg: '#B91C1C' },
  clarification_received:   { bg: '#ECFDF5', fg: '#047857' },
  pending:                  { bg: '#EFF6FF', fg: '#1D4ED8' },
  in_progress:              { bg: '#ECFDF5', fg: '#047857' },
  open:                     { bg: '#EFF6FF', fg: '#1D4ED8' },
  qc_review:                { bg: '#FEF3C7', fg: '#92400E' },
  typing:                   { bg: '#CFFAFE', fg: '#155E75' },
  mail_away:                { bg: '#ECFEFF', fg: '#0E7490' },
  mail_away_received:       { bg: '#F0FDFA', fg: '#0F766E' },
};

const formatStatusLabel = (status) =>
  (status || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const formatCreatedAt = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return formatDateTimeLocal(dateStr);
  } catch {
    return dateStr;
  }
};

/* ---------- Label / Value sub-components ---------- */
const FieldLabel = ({ children }) => (
  <div
    className="text-[11px] uppercase tracking-[0.05em]"
    style={{ color: '#9CA3AF' }}
  >
    {children}
  </div>
);

const FieldValue = ({ children, link, onClick }) =>
  link ? (
    <button
      type="button"
      onClick={onClick}
      className="text-[14px] font-semibold hover:underline transition-colors"
      style={{ color: '#4F46E5' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = '#4338CA')}
      onMouseLeave={(e) => (e.currentTarget.style.color = '#4F46E5')}
      data-testid="duplicate-modal-order-number-link"
    >
      {children}
    </button>
  ) : (
    <div className="text-[13px] font-medium" style={{ color: '#111827' }}>
      {children}
    </div>
  );

const StatusPill = ({ status }) => {
  const key = (status || '').toLowerCase();
  const palette = STATUS_PILL[key] || { bg: '#F3F4F6', fg: '#374151' };
  return (
    <span
      className="inline-flex items-center justify-center h-[22px] rounded-full text-[12px] font-medium"
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        padding: '0 10px',
      }}
      data-testid="duplicate-modal-status-pill"
    >
      {formatStatusLabel(status)}
    </span>
  );
};

/* ---------- Main component ---------- */
const DuplicateOrderModal = ({
  isOpen,
  onClose,
  existingOrder,
  onViewExisting,
  onOverride,
  canOverride = false,
  confirmBeforeOverride = true, // Optional safety confirm (spec §6)
  // DUP-UNIFY-001 (Jul 2026): terminal=true → existing order is Completed/
  // Cancelled. Warning + explicit "Create Anyway" only, NO reason required.
  // terminal=false (active match) → override requires a mandatory reason
  // (≥5 chars) which is passed to onOverride(reason).
  terminal = false,
}) => {
  // Two-stage UX: default is the review screen; clicking Override flips the
  // modal into an in-place confirmation step (spec Feb 22, 2026 — replaced the
  // prior window.confirm() prompt with an in-modal confirmation per product).
  const [confirmStage, setConfirmStage] = React.useState(false);
  const [overrideReason, setOverrideReason] = React.useState('');

  // Reset confirm stage whenever the modal closes so reopening starts fresh.
  React.useEffect(() => {
    if (!isOpen) {
      setConfirmStage(false);
      setOverrideReason('');
    }
  }, [isOpen]);

  if (!existingOrder) return null;

  const reasonValid = terminal || overrideReason.trim().length >= 5;

  const requestOverride = () => {
    if (confirmBeforeOverride) {
      setConfirmStage(true);
    } else {
      onOverride?.(terminal ? null : overrideReason.trim());
    }
  };

  const confirmOverride = () => {
    if (!reasonValid) return;
    setConfirmStage(false);
    onOverride?.(terminal ? null : overrideReason.trim());
  };

  const cancelConfirm = () => setConfirmStage(false);

  const handleViewClick = () => {
    onViewExisting?.(existingOrder.id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        // Spec: 640px desktop, 92vw mobile, 16px radius, 24px pad, custom shadow
        className="p-0 gap-0 border-0 sm:max-w-[640px] w-[92vw] max-h-[92vh] overflow-y-auto"
        style={{
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          backgroundColor: '#FFFFFF',
        }}
        hideCloseButton
        data-testid="duplicate-order-modal"
      >
        <div className="p-6">
          {/* ──────────────────────────── Header ──────────────────────────── */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              {/* Icon tile */}
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '10px',
                  color: '#D97706',
                }}
                aria-hidden="true"
              >
                <AlertTriangle className="h-[18px] w-[18px]" strokeWidth={2.25} />
              </div>

              <div className="min-w-0">
                <h2
                  className="text-[18px] font-semibold leading-tight"
                  style={{ color: '#111827' }}
                  data-testid="duplicate-modal-title"
                >
                  {terminal ? 'Matching Order Already Completed' : 'Duplicate Order Detected'}
                </h2>
                <p
                  className="text-[13px] leading-[1.5] mt-1"
                  style={{ color: '#6B7280' }}
                >
                  {terminal
                    ? 'A matching order exists but is completed or cancelled. Confirm you intend to create a new order.'
                    : 'An active order already exists for this property, client, and product.'}
                </p>
              </div>
            </div>

            {/* Close (X) */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex items-center justify-center flex-shrink-0 transition-colors"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                color: '#6B7280',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              data-testid="duplicate-modal-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* ───────────────────────── Warning Card ───────────────────────── */}
          <div
            className="mt-4 flex items-start gap-3"
            style={{
              padding: '14px 16px',
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A',
              borderRadius: '12px',
            }}
            role="status"
          >
            <ShieldAlert
              className="h-[18px] w-[18px] flex-shrink-0 mt-[2px]"
              style={{ color: '#D97706' }}
              aria-hidden="true"
            />
            <p
              className="text-[13px] leading-[1.5] m-0"
              style={{ color: '#92400E' }}
            >
              Please review the existing order before creating another one.
              Duplicate orders may affect billing accuracy, reporting, and SLA tracking.
            </p>
          </div>

          {/* ─────────────────────── Existing Order Card ─────────────────────── */}
          <div
            className="mt-4"
            style={{
              padding: '18px',
              border: '1px solid #E5E7EB',
              borderRadius: '14px',
              backgroundColor: '#FFFFFF',
            }}
            data-testid="duplicate-modal-existing-order-card"
          >
            <h3
              className="text-[14px] font-semibold m-0 mb-3"
              style={{ color: '#111827' }}
            >
              Existing Order Details
            </h3>

            {/* 2-col details grid, collapses to 1 col < 640px */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2"
              style={{ rowGap: '12px', columnGap: '24px' }}
            >
              {/* Left column */}
              <div>
                <FieldLabel>Order Number</FieldLabel>
                <FieldValue link onClick={handleViewClick}>
                  {existingOrder.order_number || 'N/A'}
                </FieldValue>
              </div>

              <div>
                <FieldLabel>Status</FieldLabel>
                <div className="mt-[2px]">
                  <StatusPill status={existingOrder.status} />
                </div>
              </div>

              <div>
                <FieldLabel>Property Address</FieldLabel>
                <FieldValue>{existingOrder.property_address || '—'}</FieldValue>
              </div>

              <div>
                <FieldLabel>Product</FieldLabel>
                <FieldValue>{existingOrder.client_product || '—'}</FieldValue>
              </div>

              <div>
                <FieldLabel>Client Code</FieldLabel>
                <FieldValue>{existingOrder.client_code || '—'}</FieldValue>
              </div>

              {existingOrder.assignee_name ? (
                <div>
                  <FieldLabel>Assignee</FieldLabel>
                  <FieldValue>{existingOrder.assignee_name}</FieldValue>
                </div>
              ) : (
                <div>
                  <FieldLabel>Assignee</FieldLabel>
                  <FieldValue>
                    <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
                      Unassigned
                    </span>
                  </FieldValue>
                </div>
              )}

              <div>
                <FieldLabel>Created</FieldLabel>
                <FieldValue>{formatCreatedAt(existingOrder.created_at)}</FieldValue>
              </div>

              {existingOrder.priority ? (
                <div>
                  <FieldLabel>Priority</FieldLabel>
                  <FieldValue>
                    {formatStatusLabel(existingOrder.priority)}
                  </FieldValue>
                </div>
              ) : null}
            </div>
          </div>

          {/* ─────────────────────── Confirm-stage Banner ─────────────────────── */}
          {/* Shown in place of the standard footer when the user clicks Override &
              Create. Spec Feb 22, 2026 — replaces the browser window.confirm() with
              an in-modal confirmation step applying to Admin and Order Desk alike. */}
          {confirmStage ? (
            <div
              className="mt-5"
              style={{
                padding: '16px 18px',
                backgroundColor: '#FFF7ED',
                border: '1px solid #FED7AA',
                borderRadius: '12px',
              }}
              data-testid="duplicate-modal-confirm-panel"
              role="alertdialog"
              aria-labelledby="duplicate-confirm-title"
            >
              <div className="flex items-start gap-3">
                <ShieldAlert
                  className="h-[18px] w-[18px] flex-shrink-0 mt-[2px]"
                  style={{ color: '#C2410C' }}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p
                    id="duplicate-confirm-title"
                    className="text-[14px] font-semibold m-0"
                    style={{ color: '#7C2D12' }}
                  >
                    Are you sure you want to create a duplicate order?
                  </p>
                  <p
                    className="text-[13px] leading-[1.5] m-0 mt-1"
                    style={{ color: '#9A3412' }}
                  >
                    This may impact billing accuracy and SLA tracking. Your
                    {terminal ? ' decision' : ' override'} will be recorded in the order audit log.
                  </p>
                </div>
              </div>

              {/* DUP-UNIFY-001: mandatory reason for ACTIVE overrides only */}
              {!terminal && (
                <div className="mt-3">
                  <label
                    htmlFor="duplicate-override-reason"
                    className="text-[13px] font-medium block mb-1"
                    style={{ color: '#7C2D12' }}
                  >
                    Override reason <span style={{ color: '#DC2626' }}>*</span>{' '}
                    <span style={{ fontWeight: 400, color: '#9A3412' }}>(minimum 5 characters)</span>
                  </label>
                  <textarea
                    id="duplicate-override-reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    rows={2}
                    placeholder="Why is a duplicate order required? e.g. client re-ordered after cancellation of the wrong file"
                    className="w-full text-[13px]"
                    style={{
                      padding: '8px 10px',
                      border: `1px solid ${reasonValid || !overrideReason ? '#FED7AA' : '#FCA5A5'}`,
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      color: '#111827',
                      resize: 'vertical',
                    }}
                    data-testid="duplicate-modal-override-reason"
                  />
                  {!reasonValid && (
                    <p className="text-[12px] m-0 mt-1" style={{ color: '#DC2626' }}
                       data-testid="duplicate-modal-reason-error">
                      A reason of at least 5 characters is required.
                    </p>
                  )}
                </div>
              )}

              <div
                className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center"
                style={{ gap: '10px' }}
              >
                <button
                  type="button"
                  onClick={cancelConfirm}
                  className="inline-flex items-center justify-center text-[14px] transition-colors w-full sm:w-auto"
                  style={{
                    height: '40px',
                    padding: '0 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                    color: '#111827',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                  data-testid="duplicate-modal-confirm-cancel"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmOverride}
                  disabled={!reasonValid}
                  className="inline-flex items-center justify-center gap-2 text-[14px] font-medium transition-all w-full sm:w-auto"
                  style={{
                    height: '40px',
                    padding: '0 18px',
                    borderRadius: '10px',
                    border: 'none',
                    color: '#FFFFFF',
                    background: reasonValid
                      ? 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)'
                      : '#C7D2FE',
                    boxShadow: reasonValid ? '0 6px 18px rgba(79,70,229,0.25)' : 'none',
                    cursor: reasonValid ? 'pointer' : 'not-allowed',
                  }}
                  onMouseEnter={(e) => {
                    if (reasonValid) e.currentTarget.style.background =
                      'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)';
                  }}
                  onMouseLeave={(e) => {
                    if (reasonValid) e.currentTarget.style.background =
                      'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)';
                  }}
                  data-testid="duplicate-modal-confirm-override"
                >
                  <ShieldAlert className="h-4 w-4" />
                  {terminal ? 'Create Anyway' : 'Confirm & Create'}
                </button>
              </div>
            </div>
          ) : (

          /* ──────────────────────────── Footer ──────────────────────────── */
          <div
            className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center"
            style={{ gap: '10px' }}
          >
            {/* Ghost Cancel (left on desktop, bottom on mobile) */}
            <button
              type="button"
              onClick={onClose}
              className="text-[14px] transition-colors"
              style={{
                height: '40px',
                padding: '0 12px',
                color: '#6B7280',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#111827')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
              data-testid="duplicate-modal-cancel"
            >
              Cancel
            </button>

            {/* Right-aligned action group */}
            <div className="flex flex-col sm:flex-row sm:items-center" style={{ gap: '10px' }}>
              {/* View Existing Order (outline) */}
              <button
                type="button"
                onClick={handleViewClick}
                className="inline-flex items-center justify-center gap-2 text-[14px] transition-colors w-full sm:w-auto"
                style={{
                  height: '40px',
                  padding: '0 16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  backgroundColor: '#FFFFFF',
                  color: '#111827',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                data-testid="duplicate-modal-view"
              >
                <ExternalLink className="h-4 w-4" />
                View Existing Order
              </button>

              {/* Override & Create (primary indigo gradient) — only when canOverride */}
              {canOverride && (
                <button
                  type="button"
                  onClick={requestOverride}
                  className="inline-flex items-center justify-center gap-2 text-[14px] font-medium transition-all w-full sm:w-auto"
                  style={{
                    height: '40px',
                    padding: '0 18px',
                    borderRadius: '10px',
                    border: 'none',
                    color: '#FFFFFF',
                    background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                    boxShadow: '0 6px 18px rgba(79,70,229,0.25)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)';
                  }}
                  data-testid="duplicate-modal-override"
                >
                  <ShieldAlert className="h-4 w-4" />
                  {terminal ? 'Create Anyway' : 'Override & Create'}
                </button>
              )}
            </div>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateOrderModal;
